import { ApiQueryParams } from '@frontend/api-utils';
import { ChartBinSize, ChartQueryParams } from '@frontend/chart/types';
import { useMemo } from 'react';

import { useInsightDetailFilters } from './insight-detail-filters.context';

export interface InsightFetchRange {
    after: string;
    before: string;
    /** For heatmaps that need an aligned start; equals `after` when filters are active. */
    rangeStart: string;
    binSize: ChartBinSize | undefined;
    /** Entity filter query params for ChartClient / list clients. */
    entityParams: ApiQueryParams<ChartQueryParams>;
    /** Stable dependency key for effects. */
    key: string;
}

/**
 * Resolves after/before/(optional) binSize + entity params for insight fetches.
 * On the overview, uses `getDefaultRange` and empty entity params.
 * On detail with active filters, uses URL-guarded values.
 */
export const useInsightFetchRange = (
    getDefaultRange: () => { after: string; before: string; rangeStart?: string },
    options?: { defaultBinSize?: ChartBinSize; useDetailBinSize?: boolean }
): InsightFetchRange => {
    const filters = useInsightDetailFilters();
    const useDetailBinSize = options?.useDetailBinSize ?? false;

    return useMemo(() => {
        if (filters.active) {
            const binSize = useDetailBinSize ? filters.binSize : options?.defaultBinSize;
            const after = filters.showDateRange ? filters.after : getDefaultRange().after;
            const before = filters.showDateRange ? filters.before : getDefaultRange().before;
            const rangeStart = filters.showDateRange ? filters.after : (getDefaultRange().rangeStart ?? after);
            return {
                after,
                before,
                rangeStart,
                binSize,
                entityParams: filters.entityParams,
                key: `${after}|${before}|${binSize ?? ''}|${JSON.stringify(filters.entityParams)}`
            };
        }

        const defaults = getDefaultRange();
        const binSize = options?.defaultBinSize;
        return {
            after: defaults.after,
            before: defaults.before,
            rangeStart: defaults.rangeStart ?? defaults.after,
            binSize,
            entityParams: {},
            key: `${defaults.after}|${defaults.before}|${binSize ?? ''}|`
        };
    }, [
        filters.active,
        filters.showDateRange,
        filters.after,
        filters.before,
        filters.binSize,
        filters.entityParams,
        getDefaultRange,
        options?.defaultBinSize,
        useDetailBinSize
    ]);
};
